<template>
  <b-container class="page-container">
    <LoadingIndicator 
      v-if="loading"
      topic="Meeting"
    />
    <BaseMessageBlock :messages="errors" />
    <div v-if="!loading && meeting !== null">
      <ProjectDetails
        class="order-0"
        :projectName="projectName"
        :meetingName="meeting.name"
        :projectUsers="projectUsers"
        :projectId="projectId"
        :organizationLogo="projectSummary.project.primaryContractor.organizationLogo"
        viewProjectBtn
      />
      <b-row class="widget-row">
        <b-col class="widget-col">
          <KickoffAttendees
            v-if="meetingUsers != []"
            :status="meeting.status"
            :meetingUsers="meetingUsers"
            :projectUsers="allProjectUsers"
            :projectTags="projectTags"
            :projectId="projectId"
            :organizations="allOrganizations"
            @addAttendee="addAttendee"
          />
        </b-col>
      </b-row>
      <b-row class="widget-row">
        <b-col class="widget-col">
          <KickoffGoals
           :projectId="projectId"
           :projectName="projectName"
           :meetingId="meetingId"
           :status="meeting.status"
           @goalsUpdated="updateGoals"
          />
        </b-col>
      </b-row>
      <b-row class="widget-row">
        <b-col class="widget-col">
          <KickoffRisks
           :projectId="projectId"
           :meetingId="meetingId"
           :status="meeting.status"
           :presetTags="projectTags"
          />
        </b-col>
      </b-row>
      <b-row class="widget-row">
        <b-col class="widget-col">
          <MeetingNotes
            v-if="meetingNotes != []"
            :projectId="projectId"
            :meetingId="meetingId"
            :meetingComments="meetingNotes"
            :status="meeting.status"
            :isMeetingOpen="isMeetingOpen"
          />
        </b-col>
      </b-row>
      <b-row class="mt-3 mb-5">
        <b-col class="text-center">
          <b-button
            v-if="isMeetingOpen"
            variant="primary"
            @click="submitMeeting"
            :disabled="!hasGoals"
            >Complete Meeting</b-button
          >
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import axios from "axios";

import ProjectDetails from "@/components/project/ProjectDetails.vue";
import KickoffAttendees from "@/components/KickoffAttendees.vue";
import KickoffGoals from "@/components/KickoffGoals.vue";
import KickoffRisks from "@/components/KickoffRisks.vue";
import MeetingNotes from "@/components/meeting/MeetingNotes.vue";

export default {
  name: "MeetingKickoff",
  components: {
    ProjectDetails,
    KickoffAttendees,
    KickoffGoals,
    KickoffRisks,
    MeetingNotes
  },
  data() {
    return {
      allProjectUsers: [],
      errors: [],
      projectId: null,
      projectName: null,
      projectSummary: null,
      meetingId: "",
      meeting: null,
      meetingNotes: [],
      meetingUsers: [],
      allOrganizations: [],
      loading: false,
      sending: false,
      goals: [],
    };
  },
  mounted() {
    this.loading = true;
    let projectId = this.$route.params.id;
    this.meetingId = this.$route.params.meetingId;
    if (!projectId || !this.meetingId) {
      this.errors.push("Meeting could not be found");
      this.loading = false;
      return;
    }

    let projectGetUrl = `${this.$api_url}/projects/${projectId}/summary?guest=true`;
    let meetingGetUrl = `${this.$api_url}/meetings/${this.meetingId}`;
    let loadingProject = true;
    let loadingMeeting = true;
    
    axios
      .get(projectGetUrl, this.getReqConfig())
      .then((response) => {
        this.setupProjectData(response.data);
        if (this.meeting) {
          this.setupMeetingUsers(this.projectUsers);
        }
      })
      .catch((err) => {
        this.errors.push(err);
      })
      .finally(() => {
        loadingProject = false;
        if (!loadingProject && !loadingMeeting) {
          this.loading = false;
        }
      });

    axios
      .get(meetingGetUrl, this.getReqConfig())
      .then((response) => {
        this.setupMeetingData(response.data);
        if (this.projectUsers) {
          this.setupMeetingUsers(this.projectUsers);
        }
      })
      .catch((err) => {
        this.errors.push(err);
      })
      .finally(() => {
        loadingMeeting = false;
        if (!loadingProject && !loadingMeeting) {
          this.loading = false;
        }
      });
  },
  methods: {
    setupProjectData(projectSummary) {
      this.projectId = projectSummary.project.projectId;
      this.projectName = projectSummary.project.projectName;
      this.periodName = projectSummary.periodName;
      this.projectTags = projectSummary.project.presetTags;
      this.projectMetrics = projectSummary.projectMetrics;
      this.projectUsers = projectSummary.projectUsers.filter(u => u.role !== "Guest");
      this.loadAllUsers();
      this.loadAllOrganizations();
      this.surveyId = projectSummary.project.survey.surveyId;

      this.projectSummary = projectSummary;

      this.comments = projectSummary.comments
        .sort((a, b) => (a.answerIndex < b.answerIndex ? 1 : -1))
        .sort((a, b) => (a.questionText > b.questionText ? 1 : -1));

      this.scheduledMeetings = projectSummary.meetings.filter(
        (m) =>
          m.periodName !== projectSummary.periodName &&
          m.start !== null &&
          new Date(m.start) > new Date()
      );

      this.byQuestion = this.$helpers.mapDataPointToChartDataSet(
        projectSummary.responsesByQuestion
      );
    },
    loadAllUsers() {
      axios
        .get(`${this.$api_url}/projects/${this.projectId}/users?inactiveUsers=true`, this.getReqConfig())
        .then(response => {
          this.allProjectUsers = response.data;
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    loadAllOrganizations() {
      axios
        .get(`${this.$api_url}/organizations`, this.getReqConfig())
        .then((response) => {
          this.allOrganizations = response.data;
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    loadProjectMetrics(next) {
      axios
        .get(
          `${this.$api_url}/projects/${this.projectId}/project-metrics`,
          this.getReqConfig()
        )
        .then((response) => {
          this.projectMetrics = response.data;
          if (next) {
            next();
          }
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    setupMeetingUsers(projectUsers) {
      let usersFromProjectUsers = projectUsers.map((u) => ({
        userId: u.userId,
        name: u.name,
        email: u.email,
        role: u.role,
        jobTitle: u.jobTitle,
        organizationLogo: u.organizationLogo,
        permissionLevel: u.permissionLevel,
        attending: false,
        actionInProgress: false,
      }));

      let usersFromMeeting = this.meeting.attendees.map((a) => ({
        userId: a.userId,
        name: a.name,
        email: a.email,
        role: a.role,
        jobTitle: a.jobTitle,
        organizationLogo: a.organizationLogo,
        permissionLevel: a.permissionLevel,
        attending: true,
        actionInProgress: false,
      }));
      let attendeeIds = usersFromMeeting.map((u) => u.userId);

      //Merge the two lists and filter out the respondents who are attending
      this.meetingUsers = usersFromMeeting.concat(
        usersFromProjectUsers.filter((u) => !attendeeIds.includes(u.userId))
      ).sort((a,b) => this.$helpers.compareAlpha(a.name,b.name));
    },
    setupMeetingData(meeting) {
      this.meeting = meeting;
      this.newProjectMetrics = meeting.newProjectMetrics;
      this.closedProjectMetrics = meeting.closedProjectMetrics;
      this.meetingDocuments = meeting.documents;
      this.facilitatorSurvey = meeting.facilitatorSurvey;
      this.meetingComments = meeting.comments.sort((a, b) => (a.dateCreated < b.dateCreated ? 1 : -1));
      if(this.meetingComments != null){
         this.dateFormat(this.meetingComments)
       }
      this.meetingNotes = meeting.notes.sort((a, b) => (a.date < b.date ? 1 : -1));
      if(this.meetingNotes != null){
        this.dateFormat(this.meetingNotes)
      }
    },
    dateFormat(commentDates) {
      if(commentDates != null){
        let newData = [];
          let line = [];
        commentDates.forEach((value, index ) => {
          value.dateCreated = new Date(value.dateCreated).setHours(new Date(value.dateCreated).getHours() - 8);
          newData.push(value);
          line.unshift(
          newData.toLocaleString("default", {
            hour: "2-digit",
            minute: "2-digit"
          })
          + ", " +
          newData.toLocaleString("default", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }));
        });
      return line;
      }
      return;
    },
    refreshProjectUsers() {
      this.loadingUsers = true;
      axios
        .get(
          `${this.$api_url}/projects/${this.projectId}/users`,
          this.getReqConfig()
        )
        .then((response) => {
          this.projectUsers = response.data;
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          this.loadingUsers = false;
        });
    },
    refreshAddUserData() {
      this.refreshProjectUsers();
      this.loadAllUsers();
      //Adding a user can also create an organization, so they need to be updated!
      this.loadAllOrganizations();
    },
    addAttendee(user) {
      user.actionInProgress = true;

      const url = `${this.$api_url}/meetings/${this.meeting.meetingId}/add-attendee/${user.userId}`;
      axios
        .post(
          url,
          null,
          this.getReqConfig()
        )
        .then(() => {
          if (!this.hasUserBeenAddedToMeeting(user)) {
            this.addUserToMeetingUsers(user);
          }
          user.attending = true;
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          user.actionInProgress = false;
        });
    },
    hasUserBeenAddedToMeeting(user) {
      return this.meetingUsers.filter(u => u.userId === user.userId).length > 0;
    },
    addUserToMeetingUsers(user) {
      const meetingUser = {
        userId: user.userId,
        name: user.name,
        email: user.email,
        role: user.role,
        jobTitle: user.jobTitle,
        permissionLevel: user.permissionLevel,
        attending: true,
        actionInProgress: false,
      };
      meetingUser.organizationLogo = null;
      this.meetingUsers.push(meetingUser);
      this.meetingUsers.sort((a,b) => this.$helpers.compareAlpha(a.name,b.name));
    },
    handleAttendeeRemove(user) {
      user.actionInProgress = true;
      axios
        .post(
          `${this.$api_url}/meetings/${this.meeting.meetingId}/delete-attendee/${user.userId}`,
          null,
          this.getReqConfig()
        )
        .then(() => {
          user.attending = false;
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          user.actionInProgress = false;
        });
    },
    submitMeeting() {
      let request = {
        meetingId: this.meeting.meetingId,
        status: "Closed"
      };

      axios
        .post(`${this.$api_url}/meeting/update`, request, this.getReqConfig())
        .then(() => {
          this.$router.push({
            name: "ProjectOverview",
            params: { id: this.projectId },
          });
        })
        .catch((err) => {
          this.errors.push(err);
        });
    },
    openModal(ref) {
      this.$refs[ref].show();
    },
    handleError(err) {
      this.errors.push(...this.$helpers.formatErrors(err));
    },
    updateGoals(goals) {
      this.goals = goals;
    }
  },
  computed: {
    isMeetingOpen() {
      return this.meeting.status !== "Closed";
    },
    attendees() {
      return this.meetingUsers.filter((u) => u.attending);
    },
    hasGoals() {
      return this.goals.length > 0;
    }
  },
};
</script>

<style lang="scss" scoped>
</style>